import styled from 'styled-components';

import { bidiText } from 'components/bidi';

/**
 * The design's two-panel transfer — `.transfer` / `.tpanel`.
 *
 * What it replaces: two `#f7f8fc` boxes under centred grey pill headers, rows
 * on `box-shadow: 0 0 0 1px #e1dfec` with antd checkboxes repainted `#e8c9bf`,
 * and a `#f6e6e0` peach button reading "Add to Saudi Coffee" — a colour on no
 * palette, on the one control that does the screen's work. Neither panel said
 * how many things were in it, the add button gave no sign of whether anything
 * was selected, and the added side was blank when empty.
 */
export const Transfer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s4);

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  overflow: hidden;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s2) var(--s4);
  border-bottom: 1px solid var(--line);
  background: var(--surface-alt);
  font-family: var(--font-ui);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  color: var(--text-muted);
`;

/** The count. Mono, at the far end — the design's `.tpanel-head .n`. */
export const Count = styled.span`
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: var(--fs-mono);
  font-variant-numeric: tabular-nums;
  color: var(--text-faint);
`;

export const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s3) var(--s3) 0;

  /* The search box takes the slack; the segmented keeps its own width. */
  > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  padding: var(--s3);
  min-height: 210px;
  max-height: var(--h-overlay-max);
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--line-strong);
    border-radius: var(--r-pill);
    border: 2px solid var(--surface);
  }
`;

/**
 * The design's `.check` row, on both sides of the transfer.
 *
 * One row shape for "a customization", whether it is waiting to be added or
 * already added, so the two panels read as two states of one list rather than
 * as two different objects.
 */
export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s3);
  padding: var(--s2) var(--s3);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface);
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s;

  &:hover {
    border-color: var(--line-strong);
    background: var(--surface-alt);
  }

  &.selected {
    border-color: var(--primary);
    background: var(--primary-soft);
  }

  /* An added row is not a choice — it carries its own controls instead. */
  &.static {
    cursor: default;

    &:hover {
      border-color: var(--line);
      background: var(--surface);
    }
  }

  /*
   * Switched off in the Customizations section, so it cannot be switched on
   * for one menu item either. The design dims it rather than hiding it — the
   * admin needs to know it exists and where to go to enable it.
   */
  &.off {
    background: var(--surface-alt);
  }
`;

export const Box = styled.span`
  display: grid;
  place-items: center;
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  border: 1.5px solid var(--line-strong);
  border-radius: 4px;
  background: var(--surface);

  svg {
    width: 10px;
    height: 10px;
    stroke: var(--text-invert);
    stroke-width: 3.2;
    opacity: 0;
  }

  .selected & {
    background: var(--primary);
    border-color: var(--primary);
  }

  .selected & svg {
    opacity: 1;
  }
`;

export const Name = styled.span`
  ${bidiText};
  min-width: 0;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .off & {
    color: var(--text-faint);
  }
`;

/** The option type — the design's `.check .ty`, mono at the far end. */
export const Type = styled.span`
  margin-left: auto;
  flex: 0 0 auto;
  font-family: var(--font-mono);
  font-size: var(--fs-micro);
  color: var(--text-faint);
`;

/** The per-item controls on an added row: its own switch, then remove. */
export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  margin-left: auto;
  flex: 0 0 auto;
`;

export const Remove = styled.button`
  display: grid;
  place-items: center;
  width: var(--h-btn-sm);
  height: var(--h-btn-sm);
  padding: 0;
  border: none;
  border-radius: var(--r-sm);
  background: none;
  color: var(--text-faint);
  cursor: pointer;
  transition:
    background-color 0.12s,
    color 0.12s;

  &:hover {
    background: var(--danger-soft);
    color: var(--danger);
  }

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.8;
  }
`;

/** The add button sits under the two panels, centred, as the design draws it. */
export const AddRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--s4);
`;

/** The design's `.actionbar`, on the card's foot. */
export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--s2);
  margin: var(--s5) calc(var(--s5) * -1) calc(var(--s5) * -1);
  padding: var(--s4) var(--s5);
  border-top: 1px solid var(--line);
  background: var(--surface-alt);
`;
